import type {
  AnySuggestedEdit,
  CategorySuggestedEdit,
  PlaybookSuggestedEdit,
  PositionSuggestedEdit,
  RuleSuggestedEdit,
  SuggestedEditKind,
  SuggestedEditPart,
  UUID,
} from '@aline/common';
import {
  CategorySuggestedEditSchema,
  getIsOwnAddSuggestion,
  getSuggestedEditParts,
  PlaybookSuggestedEditSchema,
  PositionSuggestedEditSchema,
  RuleSuggestedEditSchema,
  sortPlaybookRulePositionsByRank,
  SUGGESTED_EDIT_KINDS,
  SUGGESTED_EDIT_PARTS,
} from '@aline/common';
import { Apollo } from 'src/gql/generated';
import type { z } from 'zod';

import type { PlaybookDetail, PlaybookDetailCategory, PlaybookDetailRule } from '../types';

/**
 * The review model the playbook editor renders from: every pending suggestion
 * parsed off the JSON scalar, and the ordered list of stops the suggestions
 * bar steps through.
 *
 * A stop is a resolvable part, addressed the way the mutations address it: the
 * entity plus the part. A `change` carrying a patch and a move is two stops.
 * An `add` is a stop only on the topmost added row: the rules and positions
 * born with a new category are the category's decision, so they get no stop
 * and no chip of their own (`getIsOwnAddSuggestion`). Stops run in tree order
 * (playbook, then categories top to bottom, rules within each, positions
 * within each), one review pass down the page.
 */

export type SuggestionEntityType = 'category' | 'playbook' | 'position' | 'rule';

export interface SuggestionStop {
  /** Why accepting is refused for now, or null when it can be applied. */
  blockedReason: string | null;
  /** The category the stop sits in (its own id for a category stop). */
  categoryId: UUID | null;
  entityId: UUID;
  entityType: SuggestionEntityType;
  key: string;
  kind: SuggestedEditKind;
  part: SuggestedEditPart;
  /** The agent's reason for this part, shown when its chip is hovered. */
  rationale: string;
  /**
   * The rule the details panel opens. A rule or position stop sits in that
   * rule. A category add opens the category's first rule. A rename, a delete,
   * and the playbook stop open no rule.
   */
  ruleId: UUID | null;
  target: Apollo.PlaybookSuggestedEditTargetInput;
  /**
   * The stop's entity's place in the page walk (playbook, then each category,
   * its rules and their positions). Compared with `ruleTreeIndexes` to find
   * the first stop at or below an open rule.
   */
  treeIndex: number;
}

export interface RuleGhost {
  categoryId: UUID;
  rank: string;
  rule: PlaybookDetailRule;
}

export interface CategoryGhost {
  category: PlaybookDetailCategory;
  rank: string;
}

export interface PlaybookSuggestionModel {
  categoryEdits: Map<UUID, CategorySuggestedEdit>;
  categoryGhosts: CategoryGhost[];
  playbookEdit: PlaybookSuggestedEdit | null;
  positionEdits: Map<UUID, PositionSuggestedEdit>;
  /** The category each rule is filed under, for reading a rule's context from its id alone. */
  ruleCategoryIds: Map<UUID, UUID>;
  /** A rule's chip aggregates its positions: a rule with an untouched title but a patched position reads as edited. */
  ruleChipKinds: Map<UUID, SuggestedEditKind>;
  /** The reasons behind a rule's chip: its own part's, or one per position edit the chip stands for. */
  ruleChipRationales: Map<UUID, string[]>;
  ruleEdits: Map<UUID, RuleSuggestedEdit>;
  /** Pending rule moves by destination category, for the ghost rows. */
  ruleGhostsByCategoryId: Map<UUID, RuleGhost[]>;
  /** Every rule's place in the page walk, on the same scale as a stop's `treeIndex`. */
  ruleTreeIndexes: Map<UUID, number>;
  stops: SuggestionStop[];
}

export const PART_TO_INPUT: Record<SuggestedEditPart, Apollo.PlaybookSuggestedEditPart> = {
  [SUGGESTED_EDIT_PARTS.add]: Apollo.PlaybookSuggestedEditPart.Add,
  [SUGGESTED_EDIT_PARTS.delete]: Apollo.PlaybookSuggestedEditPart.Delete,
  [SUGGESTED_EDIT_PARTS.move]: Apollo.PlaybookSuggestedEditPart.Move,
  [SUGGESTED_EDIT_PARTS.patch]: Apollo.PlaybookSuggestedEditPart.Patch,
};

export const ACCEPT_CATEGORY_FIRST = 'Accept the new category first.';
export const MOVE_TARGET_GONE = 'The category this rule moves into no longer exists.';

export function getStopKey(entityId: UUID, part: SuggestedEditPart): string {
  return `${entityId}:${part}`;
}

export function buildSuggestionModel(playbook: PlaybookDetail): PlaybookSuggestionModel {
  const playbookId = playbook.id;
  const categoryEdits = new Map<UUID, CategorySuggestedEdit>();
  const ruleEdits = new Map<UUID, RuleSuggestedEdit>();
  const positionEdits = new Map<UUID, PositionSuggestedEdit>();
  const ruleChipKinds = new Map<UUID, SuggestedEditKind>();
  const ruleChipRationales = new Map<UUID, string[]>();
  const ruleGhostsByCategoryId = new Map<UUID, RuleGhost[]>();
  const categoryGhosts: CategoryGhost[] = [];
  const ruleCategoryIds = new Map<UUID, UUID>();
  const ruleTreeIndexes = new Map<UUID, number>();
  const stops: SuggestionStop[] = [];
  const categoryById = new Map(playbook.categories.map(category => [category.id, category]));
  // Counts every entity the walk passes, so stops and rules share one order.
  let treeIndex = 0;

  for (const category of playbook.categories) {
    const edit = parseEdit(CategorySuggestedEditSchema, category.suggestedEdit);
    if (edit) categoryEdits.set(category.id, edit);
  }
  const getIsCategoryAdd = (categoryId: UUID) =>
    categoryEdits.get(categoryId)?.type === SUGGESTED_EDIT_KINDS.add;

  const playbookEdit = parseEdit(PlaybookSuggestedEditSchema, playbook.suggestedEdit);
  if (playbookEdit)
    stops.push({
      blockedReason: null,
      categoryId: null,
      entityId: playbookId,
      entityType: 'playbook',
      key: getStopKey(playbookId, SUGGESTED_EDIT_PARTS.patch),
      kind: playbookEdit.type,
      part: SUGGESTED_EDIT_PARTS.patch,
      rationale: playbookEdit.patch.rationale,
      ruleId: null,
      target: { playbookId },
      treeIndex,
    });

  for (const category of playbook.categories) {
    treeIndex += 1;
    const categoryEdit = categoryEdits.get(category.id);
    if (categoryEdit) {
      if (categoryEdit.type === SUGGESTED_EDIT_KINDS.change && categoryEdit.move)
        categoryGhosts.push({ category, rank: categoryEdit.move.rank });
      /**
       * The rules born with a new category have no stop of their own, so the
       * bar landing on the category would otherwise leave the details panel
       * blank. Open the first rule, in the order the list shows them.
       */
      const openedRuleId =
        categoryEdit.type === SUGGESTED_EDIT_KINDS.add ? (category.rules.at(0)?.id ?? null) : null;
      for (const part of getSuggestedEditParts(categoryEdit))
        stops.push({
          blockedReason: null,
          categoryId: category.id,
          entityId: category.id,
          entityType: 'category',
          key: getStopKey(category.id, part),
          kind: categoryEdit.type,
          part,
          rationale: getPartRationale(categoryEdit, part),
          ruleId: openedRuleId,
          target: { categoryId: category.id, playbookId },
          treeIndex,
        });
    }

    for (const rule of category.rules) {
      treeIndex += 1;
      ruleCategoryIds.set(rule.id, category.id);
      ruleTreeIndexes.set(rule.id, treeIndex);
      const ruleEdit = parseEdit(RuleSuggestedEditSchema, rule.suggestedEdit);
      if (ruleEdit) ruleEdits.set(rule.id, ruleEdit);
      const isRuleAdd = ruleEdit?.type === SUGGESTED_EDIT_KINDS.add;
      // A rule added along with its category is the category's decision, not its own.
      const isRuleOwnAdd = getIsOwnAddSuggestion(
        { suggestedEdit: ruleEdit },
        { suggestedEdit: categoryEdit }
      );
      let hasRuleStop = false;

      if (ruleEdit && (!isRuleAdd || isRuleOwnAdd)) {
        hasRuleStop = true;
        if (ruleEdit.type === SUGGESTED_EDIT_KINDS.change && ruleEdit.move) {
          const ghosts = ruleGhostsByCategoryId.get(ruleEdit.move.categoryId) ?? [];
          ghosts.push({ categoryId: ruleEdit.move.categoryId, rank: ruleEdit.move.rank, rule });
          ruleGhostsByCategoryId.set(ruleEdit.move.categoryId, ghosts);
        }
        for (const part of getSuggestedEditParts(ruleEdit)) {
          const moveTarget =
            part === SUGGESTED_EDIT_PARTS.move && ruleEdit.type === SUGGESTED_EDIT_KINDS.change
              ? ruleEdit.move
              : undefined;
          const blockedReason =
            moveTarget && !categoryById.has(moveTarget.categoryId)
              ? MOVE_TARGET_GONE
              : moveTarget && getIsCategoryAdd(moveTarget.categoryId)
                ? ACCEPT_CATEGORY_FIRST
                : null;
          stops.push({
            blockedReason,
            categoryId: category.id,
            entityId: rule.id,
            entityType: 'rule',
            key: getStopKey(rule.id, part),
            kind: ruleEdit.type,
            part,
            rationale: getPartRationale(ruleEdit, part),
            ruleId: rule.id,
            target: { playbookId, ruleId: rule.id },
            treeIndex,
          });
        }
      }

      const positions = sortPlaybookRulePositionsByRank(rule.positions);
      const parsedPositions = positions.map(position => ({
        edit: parseEdit(PositionSuggestedEditSchema, position.suggestedEdit),
        position,
      }));
      let hasPositionStop = false;
      for (const { edit, position } of parsedPositions) {
        treeIndex += 1;
        if (!edit) continue;
        positionEdits.set(position.id, edit);
        /**
         * A position added along with its rule is the rule's decision (or the
         * category's, above it), not its own.
         */
        const isPositionOwnAdd = getIsOwnAddSuggestion(
          { suggestedEdit: edit },
          { suggestedEdit: ruleEdit }
        );
        if (edit.type === SUGGESTED_EDIT_KINDS.add && !isPositionOwnAdd) continue;
        hasPositionStop = true;
        for (const part of getSuggestedEditParts(edit)) {
          stops.push({
            /**
             * Never blocked, not even the delete of a rule's last accepted
             * position: the rule is left not running and the panel says so.
             */
            blockedReason: null,
            categoryId: category.id,
            entityId: position.id,
            entityType: 'position',
            key: getStopKey(position.id, part),
            kind: edit.type,
            part,
            rationale: getPartRationale(edit, part),
            ruleId: rule.id,
            target: { playbookId, positionId: position.id, ruleId: rule.id },
            treeIndex,
          });
        }
      }

      /**
       * A move-only change gets no chip: the move strip is its whole
       * treatment, as on a position card. The chip is for an add, a delete,
       * or a patch that is the rule's own decision, or one on its positions.
       */
      const hasChipPart = getSuggestedEditParts(ruleEdit).some(
        part => part !== SUGGESTED_EDIT_PARTS.move
      );
      if (ruleEdit && hasRuleStop && hasChipPart) {
        ruleChipKinds.set(rule.id, ruleEdit.type);
        ruleChipRationales.set(rule.id, getChipRationales(ruleEdit));
      } else if (hasPositionStop) {
        ruleChipKinds.set(rule.id, SUGGESTED_EDIT_KINDS.change);
        ruleChipRationales.set(
          rule.id,
          parsedPositions.flatMap(({ edit, position }) =>
            edit && positionEdits.has(position.id) ? getChipRationales(edit) : []
          )
        );
      }
    }
  }

  return {
    categoryEdits,
    categoryGhosts,
    playbookEdit,
    positionEdits,
    ruleCategoryIds,
    ruleChipKinds,
    ruleChipRationales,
    ruleEdits,
    ruleGhostsByCategoryId,
    ruleTreeIndexes,
    stops,
  };
}

/**
 * The one thing the page has selected. A rule, from a click on its row; or a
 * stop, from the suggestions bar or a pill. A stop inside a rule opens that
 * rule, so which rule is open is derived, never stored on its own.
 */
export type PlaybookSelection =
  | { type: 'none' }
  | { ruleId: UUID; type: 'rule' }
  | {
      /** Where the stop sat when it was last seen; the fallback once it is gone. */
      index: number;
      key: string;
      /** The rule the stop opened, so the panel stays put when the last stop is decided. */
      ruleId: UUID | null;
      type: 'stop';
    };

export const NO_SELECTION: PlaybookSelection = { type: 'none' };

export function selectionFromRuleId(ruleId: UUID | null): PlaybookSelection {
  return ruleId ? { ruleId, type: 'rule' } : NO_SELECTION;
}

export function selectionFromStop(index: number, stop: SuggestionStop): PlaybookSelection {
  return { index, key: stop.key, ruleId: stop.ruleId, type: 'stop' };
}

/** What the selection points at right now, against the current list of stops. */
export interface ResolvedSelection {
  /** The current stop's place in the list, or null when the selection is not a stop. */
  currentIndex: number | null;
  currentStop: SuggestionStop | null;
  /** The rule open in the details panel. */
  selectedRuleId: UUID | null;
}

/**
 * The selected stop's current position. Found by key first, so suggestions
 * streaming in above it while the agent writes do not move the highlight.
 * Found by remembered index once the stop is gone, so deciding it lands on
 * whatever took its place, which is how a review pass moves down the page.
 * Null once there are no stops left.
 */
export function getCurrentStopIndex(
  stops: readonly Pick<SuggestionStop, 'key'>[],
  selection: { index: number; key: string }
): number | null {
  if (stops.length === 0) return null;
  const indexByKey = stops.findIndex(stop => stop.key === selection.key);
  if (indexByKey !== -1) return indexByKey;
  return Math.min(selection.index, stops.length - 1);
}

export function resolveSelection(
  model: Pick<PlaybookSuggestionModel, 'stops'>,
  selection: PlaybookSelection
): ResolvedSelection {
  if (selection.type === 'none')
    return { currentIndex: null, currentStop: null, selectedRuleId: null };
  if (selection.type === 'rule')
    return { currentIndex: null, currentStop: null, selectedRuleId: selection.ruleId };
  const currentIndex = getCurrentStopIndex(model.stops, selection);
  const currentStop = currentIndex === null ? null : (model.stops.at(currentIndex) ?? null);
  // With every stop decided, the rule the last one opened stays open.
  if (!currentStop)
    return { currentIndex: null, currentStop: null, selectedRuleId: selection.ruleId };
  return { currentIndex, currentStop, selectedRuleId: currentStop.ruleId };
}

/**
 * Where Next goes: the stop after the current one, wrapping. From an open
 * rule, the first stop at or below it on the page, so Next means "the next
 * suggestion below what I am looking at"; from nothing, the first stop.
 */
export function getNextStopIndex(
  model: Pick<PlaybookSuggestionModel, 'ruleTreeIndexes' | 'stops'>,
  selection: PlaybookSelection
): number | null {
  const { stops } = model;
  if (stops.length === 0) return null;
  if (selection.type === 'stop') {
    const currentIndex = getCurrentStopIndex(stops, selection);
    return currentIndex === null ? 0 : (currentIndex + 1) % stops.length;
  }
  const ruleTreeIndex = getRuleTreeIndex(model, selection);
  if (ruleTreeIndex === null) return 0;
  const below = stops.findIndex(stop => stop.treeIndex >= ruleTreeIndex);
  return below === -1 ? 0 : below;
}

/** Where Previous goes: the mirror of `getNextStopIndex`, with the open rule's own stops excluded. */
export function getPreviousStopIndex(
  model: Pick<PlaybookSuggestionModel, 'ruleTreeIndexes' | 'stops'>,
  selection: PlaybookSelection
): number | null {
  const { stops } = model;
  if (stops.length === 0) return null;
  const last = stops.length - 1;
  if (selection.type === 'stop') {
    const currentIndex = getCurrentStopIndex(stops, selection);
    return currentIndex === null ? last : (currentIndex - 1 + stops.length) % stops.length;
  }
  const ruleTreeIndex = getRuleTreeIndex(model, selection);
  if (ruleTreeIndex === null) return last;
  const above = stops.findLastIndex(stop => stop.treeIndex < ruleTreeIndex);
  return above === -1 ? last : above;
}

/** The stop for one entity part, if it is pending. */
export function findStop(
  model: PlaybookSuggestionModel,
  entityId: UUID,
  part: SuggestedEditPart
): SuggestionStop | null {
  const key = getStopKey(entityId, part);
  return model.stops.find(stop => stop.key === key) ?? null;
}

/** Whether a rule, or anything inside it, has a stop the bar can land on. */
export function getHasStopsInRule(model: PlaybookSuggestionModel, ruleId: UUID): boolean {
  return model.stops.some(stop => stop.ruleId === ruleId);
}

export interface RankedEntry<TItem, TGhost> {
  rank: string;
  ghost?: TGhost;
  item?: TItem;
}

/**
 * Real rows and ghost destinations in one list, ordered by rank. A move's
 * resolved rank sits between its anchor and the anchor's next sibling, so
 * sorting by rank puts each ghost exactly where the move would land.
 */
export function interleaveByRank<TItem extends { rank: string }, TGhost extends { rank: string }>(
  items: readonly TItem[],
  ghosts: readonly TGhost[]
): RankedEntry<TItem, TGhost>[] {
  const entries: RankedEntry<TItem, TGhost>[] = [
    ...items.map(item => ({ item, rank: item.rank })),
    ...ghosts.map(ghost => ({ ghost, rank: ghost.rank })),
  ];
  return entries.sort((a, b) => (a.rank < b.rank ? -1 : a.rank > b.rank ? 1 : 0));
}

/**
 * The reason behind one part of a suggestion. An add or a delete carries its
 * own; each part of a change carries its own, since the two resolve apart.
 */
function getPartRationale(edit: AnySuggestedEdit, part: SuggestedEditPart): string {
  if (edit.type !== SUGGESTED_EDIT_KINDS.change) return edit.rationale;
  if (part === SUGGESTED_EDIT_PARTS.move) return 'move' in edit ? (edit.move?.rationale ?? '') : '';
  return edit.patch?.rationale ?? '';
}

/** The reasons a chip stands for: every part but a move, which has its own strip. */
function getChipRationales(edit: AnySuggestedEdit): string[] {
  return getSuggestedEditParts(edit)
    .filter(part => part !== SUGGESTED_EDIT_PARTS.move)
    .map(part => getPartRationale(edit, part));
}

/** The JSON scalar is untyped; the zod schema in `@aline/common` is the source of truth. */
function parseEdit<TSchema extends z.ZodType>(
  schema: TSchema,
  raw: unknown
): z.infer<TSchema> | null {
  if (raw === null || raw === undefined) return null;
  const result = schema.safeParse(raw);
  return result.success ? result.data : null;
}

/** The open rule's place in the page walk; null with no rule open, or a rule no longer in the playbook. */
function getRuleTreeIndex(
  model: Pick<PlaybookSuggestionModel, 'ruleTreeIndexes'>,
  selection: PlaybookSelection
): number | null {
  if (selection.type !== 'rule') return null;
  return model.ruleTreeIndexes.get(selection.ruleId) ?? null;
}
